/**
 * Reads a table (header row + rows) from a CSV or Excel (.xlsx) file in the browser, for performance test data files.
 * Excel: the first worksheet, cells as shown text for strings and plain numbers (no formulas evaluated, no dates
 * formatted). Uses the platform's own unzip (DecompressionStream) and a small XML scanner, so no library is needed.
 */
import { parseCsv } from '@1qa/shared';

export async function readTableFile(file: File): Promise<string[][]> {
  const name = file.name.toLowerCase();
  if (name.endsWith('.xlsx')) return readXlsx(new Uint8Array(await file.arrayBuffer()));
  if (name.endsWith('.xls')) throw new Error('Old Excel files (.xls) can’t be read: save it as .xlsx or CSV and choose it again.');
  return parseCsv(await file.text());
}

/** Entries of a zip file (central directory), by name. */
function zipEntries(bytes: Uint8Array): Map<string, { method: number; size: number; offset: number }> {
  const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
  let end = -1;
  for (let i = bytes.length - 22; i >= Math.max(0, bytes.length - 65_557); i--) {
    if (view.getUint32(i, true) === 0x06054b50) {
      end = i;
      break;
    }
  }
  if (end < 0) throw new Error('This is not an Excel (.xlsx) file.');
  const count = view.getUint16(end + 10, true);
  let p = view.getUint32(end + 16, true);
  const out = new Map<string, { method: number; size: number; offset: number }>();
  const dec = new TextDecoder();
  for (let i = 0; i < count; i++) {
    if (view.getUint32(p, true) !== 0x02014b50) break;
    const method = view.getUint16(p + 10, true);
    const size = view.getUint32(p + 20, true);
    const nameLen = view.getUint16(p + 28, true);
    const extra = view.getUint16(p + 30, true);
    const comment = view.getUint16(p + 32, true);
    const local = view.getUint32(p + 42, true);
    out.set(dec.decode(bytes.subarray(p + 46, p + 46 + nameLen)), { method, size, offset: local });
    p += 46 + nameLen + extra + comment;
  }
  return out;
}

async function zipText(bytes: Uint8Array, entries: ReturnType<typeof zipEntries>, name: string): Promise<string | null> {
  const e = entries.get(name);
  if (!e) return null;
  const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
  const start = e.offset + 30 + view.getUint16(e.offset + 26, true) + view.getUint16(e.offset + 28, true);
  const raw = bytes.subarray(start, start + e.size);
  if (e.method === 0) return new TextDecoder().decode(raw);
  if (e.method !== 8) throw new Error('This Excel file is packed in a way 1QA can’t read: save it as CSV and choose it again.');
  const stream = new Blob([raw.slice()]).stream().pipeThrough(new DecompressionStream('deflate-raw'));
  return new Response(stream).text();
}

/** "B12" -> column index 1. */
function columnOf(ref: string): number {
  let n = 0;
  for (const ch of ref.replace(/\d+$/, '')) n = n * 26 + (ch.charCodeAt(0) - 64);
  return n - 1;
}

/** Text of XML character data: the five entities and numeric references. */
function unescapeXml(text: string): string {
  return text.replace(/&(#x[0-9a-f]+|#\d+|amp|lt|gt|quot|apos);/gi, (_, e: string) => {
    const k = e.toLowerCase();
    if (k[0] === '#') return String.fromCodePoint(k[1] === 'x' ? Number.parseInt(k.slice(2), 16) : Number(k.slice(1)));
    return ({ amp: '&', lt: '<', gt: '>', quot: '"', apos: "'" } as Record<string, string>)[k]!;
  });
}

/** Elements `<tag ...>inner</tag>` (any namespace prefix) with their attributes; self-closing ones have inner "". */
function elements(text: string, tag: string): { attrs: Record<string, string>; inner: string }[] {
  const re = new RegExp(`<(?:\\w+:)?${tag}(\\s[^>]*?)?(?:/>|>([\\s\\S]*?)</(?:\\w+:)?${tag}>)`, 'g');
  return [...text.matchAll(re)].map((m) => ({ attrs: Object.fromEntries([...(m[1] ?? '').matchAll(/([\w:]+)="([^"]*)"/g)].map((a) => [a[1]!, unescapeXml(a[2]!)])), inner: m[2] ?? '' }));
}

/** All `<t>` texts inside (a shared string or inline string may be split into rich-text runs). */
const texts = (inner: string) => elements(inner, 't').map((t) => unescapeXml(t.inner)).join('');

export async function readXlsx(bytes: Uint8Array): Promise<string[][]> {
  const entries = zipEntries(bytes);
  // The first sheet of the workbook (its order, not the file name).
  const workbook = await zipText(bytes, entries, 'xl/workbook.xml');
  const rels = await zipText(bytes, entries, 'xl/_rels/workbook.xml.rels');
  let sheetPath = 'xl/worksheets/sheet1.xml';
  if (workbook && rels) {
    const rid = elements(workbook, 'sheet')[0]?.attrs['r:id'];
    const target = elements(rels, 'Relationship').find((r) => r.attrs.Id === rid)?.attrs.Target;
    if (target) sheetPath = target.startsWith('/') ? target.slice(1) : `xl/${target.replace(/^\.\//, '')}`;
  }
  const sheet = await zipText(bytes, entries, sheetPath);
  if (!sheet) throw new Error('This Excel file has no worksheet 1QA can read.');
  const sharedXml = await zipText(bytes, entries, 'xl/sharedStrings.xml');
  const shared = sharedXml ? elements(sharedXml, 'si').map((si) => texts(si.inner)) : [];
  const rows: string[][] = [];
  for (const r of elements(sheet, 'row')) {
    const row: string[] = [];
    for (const c of elements(r.inner, 'c')) {
      const at = c.attrs.r ? columnOf(c.attrs.r) : row.length;
      const type = c.attrs.t;
      const v = unescapeXml(elements(c.inner, 'v')[0]?.inner ?? '');
      const value = type === 's' ? (shared[Number(v)] ?? '') : type === 'inlineStr' ? texts(c.inner) : type === 'b' ? (v === '1' ? 'TRUE' : 'FALSE') : v;
      while (row.length < at) row.push('');
      row[at] = value;
    }
    if (row.some((x) => x !== '')) rows.push(row);
  }
  return rows;
}
